/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListSessionQuarantinesRequest,
  ListSessionQuarantinesSecurity,
} from "../models/operations/listsessionquarantines.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskListSessionQuarantinesQuery,
  prefetchRiskListSessionQuarantines,
  queryKeyRiskListSessionQuarantines,
  RiskListSessionQuarantinesQueryData,
} from "./riskListSessionQuarantines.core.js";
export {
  buildRiskListSessionQuarantinesQuery,
  prefetchRiskListSessionQuarantines,
  queryKeyRiskListSessionQuarantines,
  type RiskListSessionQuarantinesQueryData,
};

export type RiskListSessionQuarantinesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listSessionQuarantines risk
 *
 * @remarks
 * List active session quarantines for the current project.
 */
export function useRiskListSessionQuarantines(
  request?: ListSessionQuarantinesRequest | undefined,
  security?: ListSessionQuarantinesSecurity | undefined,
  options?: QueryHookOptions<
    RiskListSessionQuarantinesQueryData,
    RiskListSessionQuarantinesQueryError
  >,
): UseQueryResult<
  RiskListSessionQuarantinesQueryData,
  RiskListSessionQuarantinesQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskListSessionQuarantinesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSessionQuarantines risk
 *
 * @remarks
 * List active session quarantines for the current project.
 */
export function useRiskListSessionQuarantinesSuspense(
  request?: ListSessionQuarantinesRequest | undefined,
  security?: ListSessionQuarantinesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskListSessionQuarantinesQueryData,
    RiskListSessionQuarantinesQueryError
  >,
): UseSuspenseQueryResult<
  RiskListSessionQuarantinesQueryData,
  RiskListSessionQuarantinesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskListSessionQuarantinesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskListSessionQuarantinesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskListSessionQuarantinesQueryData,
): RiskListSessionQuarantinesQueryData | undefined {
  const key = queryKeyRiskListSessionQuarantines(...queryKeyBase);

  return client.setQueryData<RiskListSessionQuarantinesQueryData>(key, data);
}

export function invalidateRiskListSessionQuarantines(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "sessionQuarantines",
      "listSessionQuarantines",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllRiskListSessionQuarantines(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "sessionQuarantines", "listSessionQuarantines"],
  });
}
